import type { Flow } from '../../contract/flow';
import type { FlowSubscription } from '../../contract/subscription';
import { withBypassSsr } from '../../utils/bypassSsrCheck';

/**
 * Представляет кешированное значение из потока, которое может быть либо успешным, либо ошибкой.
 * @internal
 */
type SourceCachedValue = { type: 'success'; value: unknown } | { type: 'error'; error: unknown };

/**
 * Абстрактный базовый класс для вычислений потоков, который управляет зависимостями,
 * подписками и кешированными значениями.
 *
 * @typeParam T - Тип значения, которое производит это вычисление
 * @typeParam C - Тип контекста для функции вычисления
 */
export abstract class FlowComputationBase<T, C> {
    /** Набор потоков, от которых зависит это вычисление */
    private sources = new Set<Flow<unknown>>();

    /** Кеш последних известных значений из каждого источника */
    private lastValues = new Map<Flow<unknown>, SourceCachedValue>();

    /** Активные подписки на источники */
    private subscriptions: FlowSubscription[] = [];

    /** Завершено ли вычисление (больше не могут быть добавлены источники) */
    protected finalized = false;

    /**
     * Текущее вычисленное значение, обернутое в объект для обработки undefined значений.
     * `null` — признак состояния ошибки, других способов его отличить нет.
     */
    protected value: { current: T } | null = null;

    /** Ошибка вычисления; значима, только когда {@link value} равно `null` */
    protected error: unknown;

    /**
     * Устанавливает вычисленное значение и очищает состояние ошибки.
     *
     * @param value - Значение, которое нужно установить как текущий результат вычисления
     */
    public setValue(value: T): void {
        this.value = { current: value };
        this.error = undefined;
    }

    /**
     * Устанавливает ошибку и очищает текущее значение.
     *
     * @param error - Ошибка, которая произошла во время вычисления
     */
    public setError(error: unknown): void {
        this.value = null;
        this.error = error;
    }

    /**
     * Получает текущее вычисленное значение.
     *
     * @returns Текущее значение
     * @throws Сохраненную ошибку, если вычисление находится в состоянии ошибки
     */
    public getValue(): T {
        // Состояние ошибки определяем по отсутствию значения, а не по truthiness самой ошибки:
        // упасть можно и falsy-значением, и бросить надо именно его
        if (this.value === null) {
            throw this.error;
        }

        return this.value.current;
    }

    /**
     * Значение вычисления в обертке, либо `null`, если вычисление закончилось ошибкой.
     *
     * В отличие от {@link getValue}, сохраненную ошибку не бросает. Обертка нужна, чтобы отличить
     * успешный `undefined` от состояния ошибки.
     *
     * @returns Обертку с текущим значением либо `null` для состояния ошибки
     */
    public peekValue(): Readonly<{ current: T }> | null {
        return this.value;
    }

    /**
     * Совпадает ли результат этого вычисления с результатом другого.
     *
     * В отличие от {@link getValue}, сохраненную ошибку не бросает: сравнение результатов
     * не должно иметь побочных эффектов.
     *
     * @param other - Вычисление, с которым сравниваем результат
     * @returns `true`, если оба вычисления дали одно и то же значение либо одну и ту же ошибку
     */
    public hasSameOutcome(other: FlowComputationBase<T, unknown>): boolean {
        // Сначала признак «значение вообще есть»: успешный `undefined` и ошибка `undefined`
        // различаются только им, по самим полям они неотличимы
        if ((this.value === null) !== (other.value === null)) {
            return false;
        }
        if (!Object.is(this.error, other.error)) {
            return false;
        }
        return Object.is(this.value?.current, other.value?.current);
    }

    /**
     * Добавляет поток как зависимость для этого вычисления.
     *
     * Потоки, добавленные после финализации, игнорируются для предотвращения
     * фантомных зависимостей.
     *
     * @param flow - Поток для добавления в качестве исходной зависимости
     */
    protected addSource(flow: Flow<unknown>): void {
        if (this.finalized) {
            return;
        }
        this.sources.add(flow);
    }

    /**
     * Кеширует успешное значение из потока.
     *
     * @param flow - Исходный поток
     * @param value - Значение для кеширования
     */
    protected setSourceValue(flow: Flow<unknown>, value: unknown): void {
        this.lastValues.set(flow, { type: 'success', value });
    }

    /**
     * Кеширует ошибку из потока.
     *
     * @param flow - Исходный поток
     * @param error - Ошибка для кеширования
     */
    protected setSourceError(flow: Flow<unknown>, error: unknown): void {
        this.lastValues.set(flow, { type: 'error', error });
    }

    /**
     * Подписывается на все собранные исходные потоки с предоставленным обработчиком.
     *
     * @param handler - Функция обратного вызова для выполнения при изменении источников
     */
    public subscribeToSources(handler: () => void): void {
        withBypassSsr(() => {
            for (const flow of this.sources) {
                this.subscriptions.push(flow.subscribe(handler));
            }
        });
    }

    /**
     * Помечает вычисление как финализированное, предотвращая добавление новых источников.
     */
    public finalize(): void {
        this.finalized = true;
    }

    /**
     * Возвращает true, если вычисление завершено (финализировано).
     *
     * Используется для различения завершённых и ещё выполняющихся вычислений:
     * синхронные вычисления финализируются немедленно, асинхронные — только
     * после завершения асинхронной операции.
     *
     * @returns `true`, если вычисление финализировано, `false` — если ещё выполняется
     */
    public isFinalized(): boolean {
        return this.finalized;
    }

    /**
     * Подготавливает объект к сборке мусора, очищая подписки и ссылки.
     */
    public dispose(): void {
        this.finalize();

        for (const subscription of this.subscriptions) {
            subscription.unsubscribe();
        }
        this.subscriptions.length = 0;

        // ПРИМЕЧАНИЕ: Мы не очищаем this.value и this.lastValues, потому что они продолжают использоваться для обнаружения изменений
    }

    /**
     * Проверяет, изменился ли поток с момента последнего кеширования его значения.
     *
     * @param source - Исходный поток для проверки изменений
     * @returns `true`, если источник изменился, `false` в противном случае
     */
    private hasSourceChanged(source: Flow<unknown>): boolean {
        const lastValue = this.lastValues.get(source);
        if (!lastValue) {
            return true;
        }

        try {
            const currentValue = source.getSnapshot();
            if (lastValue.type === 'success') {
                return !Object.is(currentValue, lastValue.value);
            }
        } catch (err) {
            if (lastValue.type === 'error') {
                return !Object.is(err, lastValue.error);
            }
        }

        return true;
    }

    /**
     * Проверяет, изменился ли какой-либо из исходных потоков с момента отписки от них.
     *
     * Этот метод полезен для определения, нужно ли повторно выполнить вычисление
     * из-за изменений в его зависимостях.
     *
     * @returns `true`, если какой-либо источник изменился, `false`, если все источники не изменились
     */
    public sourcesHasBeenChanged(): boolean {
        for (const source of this.sources) {
            if (this.hasSourceChanged(source)) {
                return true;
            }
        }
        return false;
    }

    /**
     * Создает и возвращает контекст вычисления для этого вычисления потока.
     *
     * Контекст обычно включает методы для чтения значений потоков и управления
     * выполнением вычисления.
     *
     * @returns Объект контекста вычисления
     */
    public abstract getContext(): C;

    /**
     * Читает текущее значение из потока и устанавливает его как зависимость.
     *
     * @typeParam T - Тип значения, которое производит поток
     * @param flow - Поток для чтения
     * @returns Текущее значение потока
     * @throws Ошибку потока, если он находится в состоянии ошибки
     */
    protected readFlow<T>(flow: Flow<T>): T {
        // Регистрируем поток как зависимость
        this.addSource(flow);

        try {
            const value = flow.getSnapshot();
            // Сохраняем успешное значение для отслеживания зависимостей
            this.setSourceValue(flow, value);
            return value;
        } catch (err) {
            // Сохраняем ошибку для отслеживания зависимостей
            this.setSourceError(flow, err);
            // Пробрасываем ошибку для сохранения распространения ошибок
            throw err;
        }
    }

    /**
     * Отменяет текущее вычисление, выбрасывая сигнал прерывания.
     *
     * @returns Никогда не возвращает значение - всегда выбрасывает исключение для прерывания вычисления
     * @throws Всегда выбрасывает сигнал прерывания для отмены вычисления
     */
    protected skip(): never {
        // Выбрасываем сигнал прерывания для отмены текущего вычисления
        throw AbortSignal.abort().reason;
    }

    /**
     * Обновляет кешированные значения всех исходных зависимостей,
     * гарантируя правильную работу последующих операций обнаружения изменений.
     * Обычно вызывается после отката вычисления.
     */
    public updateSourcesValue(): void {
        for (const source of this.sources) {
            try {
                this.readFlow(source);
            } catch {
                // ничего не делаем
            }
        }
    }
}
